import { useMemo, ReactNode, useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { VStack, Text, Spinner } from '@chakra-ui/react';
import { DelayedRender } from '@duke-hq/ui/components/DelayedRender';
import { FileUploadContextProvider, type FileQuantity } from './FileUploadContext';
import type {
  AcceptableFilesType,
  CompleteFileUploadOutput,
  FileVisibility,
} from '@duke-hq/svc-files-lib/schemas';
import { trpcFiles } from './trpc';
import { useFilesTrpcClient } from './FilesTrpcClientProvider';

function FileUploadContextDirectInner({
  quantity,
  accept,
  children,
  attachedToId,
  attachedToKind,
  visibility,
  maxSizePerFileMB,
  onFileComplete,
}: {
  quantity?: FileQuantity;
  accept?: AcceptableFilesType;
  children: ReactNode;
  attachedToId?: string;
  attachedToKind?: string;
  visibility?: FileVisibility;
  maxSizePerFileMB?: number;
  onFileComplete?: (output: CompleteFileUploadOutput) => void;
}) {
  const createFiles = trpcFiles.file.createFiles.useMutation();
  const createPartUploadUrl = trpcFiles.file.createPartUploadUrl.useMutation();
  const completeFileUpload = trpcFiles.file.completeFileUpload.useMutation();
  const cancelFileUpload = trpcFiles.file.cancelFileUpload.useMutation();
  const deleteFileUpload = trpcFiles.file.deleteFileUpload.useMutation();

  const actions = useMemo(() => {
    return {
      createFiles: createFiles.mutateAsync,
      createPartUploadUrl: createPartUploadUrl.mutateAsync,
      completeFileUpload: completeFileUpload.mutateAsync,
      cancelFileUpload: cancelFileUpload.mutateAsync,
      deleteFileUpload: deleteFileUpload.mutateAsync,
    };
  }, [createFiles, createPartUploadUrl, completeFileUpload, cancelFileUpload, deleteFileUpload]);

  return (
    <FileUploadContextProvider
      quantity={quantity}
      accept={accept}
      attachedToId={attachedToId}
      attachedToKind={attachedToKind}
      visibility={visibility}
      maxSizePerFileMB={maxSizePerFileMB}
      onFileComplete={onFileComplete}
      actions={actions}
    >
      {children}
    </FileUploadContextProvider>
  );
}

export function FileUploadContextDirect(props: {
  quantity?: FileQuantity;
  accept?: AcceptableFilesType;
  children: ReactNode;
  attachedToId?: string;
  attachedToKind?: string;
  visibility?: FileVisibility;
  maxSizePerFileMB?: number;
  onFileComplete?: (output: CompleteFileUploadOutput) => void;
}) {
  const { authenticationStatus, trpcClient, queryClient, initialize } = useFilesTrpcClient();
  const [state, setState] = useState({ initializing: false, hasLoaded: false });

  useEffect(() => {
    const init = async () => {
      setState({ initializing: true, hasLoaded: false });
      await initialize();
      setState({ initializing: false, hasLoaded: true });
    };

    void init();
  }, [initialize]);

  if (state.initializing) {
    return (
      <DelayedRender delayMs={2000}>
        <VStack spacing={4} p={6}>
          <Spinner size="lg" />
          <Text>Connecting to files service...</Text>
        </VStack>
      </DelayedRender>
    );
  }

  if (state.hasLoaded && authenticationStatus === 'failed') {
    return (
      <VStack spacing={4} p={6} borderWidth={1} borderColor="orange.300" borderRadius="md">
        <Text color="orange.600" fontWeight="medium">
          Files service not configured
        </Text>
        <Text fontSize="sm" color="gray.600">
          The files service endpoint is not configured in the tenant context. File uploads are
          unavailable.
        </Text>
      </VStack>
    );
  }

  return (
    <trpcFiles.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <FileUploadContextDirectInner {...props} />
      </QueryClientProvider>
    </trpcFiles.Provider>
  );
}
